import React, { useEffect, useState } from 'react';
import { setAnalyticsConsent, hasAnalyticsConsent } from '../lib/analytics';
import { useLocale } from '../i18n/LocaleContext';

const STORAGE_KEY = 'sk_analytics_consent';
const OPEN_PREFS_EVENT = 'sk:open-cookie-preferences';

const isPrerender = () =>
  typeof navigator !== 'undefined' &&
  /ReactSnap|HeadlessChrome|prerender/i.test(navigator.userAgent);

// Lets any part of the app (e.g. a footer link) reopen the cookie preferences
// so visitors can withdraw or change consent at any time, as GDPR requires.
export const openCookiePreferences = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OPEN_PREFS_EVENT));
};

const COLORS = {
  bg: '#262624',
  fg: '#f2f3d9',
  accent: '#f97b22',
  fgDim: 'rgba(242, 243, 217, 0.7)',
};

const baseButton = {
  borderRadius: '999px',
  padding: '10px 18px',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '14px',
  border: 'none',
  transition: 'opacity 0.2s ease',
};

const CookieConsent = () => {
  const { t } = useLocale();
  const [visible, setVisible] = useState(false);
  // 'main' = initial banner, 'prefs' = preferences screen
  const [view, setView] = useState('main');
  const [analyticsOn, setAnalyticsOn] = useState(false);

  useEffect(() => {
    if (isPrerender()) return;
    let stored = null;
    try {
      stored = window.localStorage.getItem(STORAGE_KEY);
    } catch (err) {
      stored = null;
    }
    if (stored === 'granted') {
      setAnalyticsConsent(true);
      return;
    }
    if (stored === 'denied') return;
    const t = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const reopen = () => {
      setAnalyticsOn(hasAnalyticsConsent());
      setView('prefs');
      setVisible(true);
    };
    window.addEventListener('sk:open-cookie-preferences', reopen);
    return () => window.removeEventListener('sk:open-cookie-preferences', reopen);
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    setAnalyticsConsent(true);
    setVisible(false);
  };

  const rejectAll = () => {
    setAnalyticsConsent(false);
    setVisible(false);
  };

  const openPrefs = () => {
    setAnalyticsOn(false);
    setView('prefs');
  };

  const savePrefs = () => {
    setAnalyticsConsent(analyticsOn);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t('cookies.title')}
      style={{
        position: 'fixed',
        zIndex: 9999,
        left: '16px',
        right: '16px',
        bottom: '16px',
        maxWidth: '520px',
        margin: '0 auto',
        padding: '22px 24px',
        background: COLORS.bg,
        color: COLORS.fg,
        borderRadius: '14px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        fontFamily: '"Space Grotesk", "Bricolage Grotesque", system-ui, sans-serif',
        fontSize: '14px',
        lineHeight: 1.45,
      }}
    >
      {view === 'main' ? (
        <>
          <div style={{ marginBottom: '6px', fontWeight: 700, fontSize: '16px' }}>
            {t('cookies.title')}
          </div>
          <div style={{ marginBottom: '16px', color: COLORS.fgDim }}>
            {t('cookies.body')}
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={acceptAll}
              style={{ ...baseButton, background: COLORS.accent, color: COLORS.bg }}
            >
              {t('cookies.accept')}
            </button>
            <button
              type="button"
              onClick={rejectAll}
              style={{
                ...baseButton,
                background: 'transparent',
                color: COLORS.fg,
                border: `1px solid ${COLORS.fg}`,
              }}
            >
              {t('cookies.reject')}
            </button>
            <button
              type="button"
              onClick={openPrefs}
              style={{
                ...baseButton,
                background: 'transparent',
                color: COLORS.fg,
                textDecoration: 'underline',
                padding: '10px 6px',
                fontWeight: 500,
              }}
            >
              {t('cookies.preferences')}
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ marginBottom: '6px', fontWeight: 700, fontSize: '16px' }}>
            {t('cookies.prefsTitle')}
          </div>
          <div style={{ marginBottom: '18px', color: COLORS.fgDim }}>
            {t('cookies.prefsBody')}
          </div>

          <PrefRow
            title={t('cookies.necessary')}
            description={t('cookies.necessaryBody')}
            checked
            disabled
          />

          <PrefRow
            title={t('cookies.analytics')}
            description={t('cookies.analyticsBody')}
            checked={analyticsOn}
            onChange={setAnalyticsOn}
          />

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '20px' }}>
            <button
              type="button"
              onClick={savePrefs}
              style={{ ...baseButton, background: COLORS.accent, color: COLORS.bg }}
            >
              {t('cookies.save')}
            </button>
            <button
              type="button"
              onClick={() => setView('main')}
              style={{
                ...baseButton,
                background: 'transparent',
                color: COLORS.fg,
                border: `1px solid ${COLORS.fg}`,
              }}
            >
              {t('cookies.back')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const PrefRow = ({ title, description, checked, onChange, disabled }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '14px',
      padding: '12px 0',
      borderTop: '1px solid rgba(242, 243, 217, 0.15)',
    }}
  >
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 600, marginBottom: '2px' }}>{title}</div>
      <div style={{ color: COLORS.fgDim, fontSize: '13px' }}>{description}</div>
    </div>
    <Toggle checked={checked} disabled={disabled} onChange={onChange} />
  </div>
);

const Toggle = ({ checked, disabled, onChange }) => {
  const handleClick = () => {
    if (disabled) return;
    onChange?.(!checked);
  };
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={handleClick}
      style={{
        flexShrink: 0,
        width: '40px',
        height: '22px',
        borderRadius: '999px',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: checked ? COLORS.accent : 'rgba(242, 243, 217, 0.25)',
        position: 'relative',
        transition: 'background 0.2s ease',
        opacity: disabled ? 0.55 : 1,
        padding: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '3px',
          left: checked ? '21px' : '3px',
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          background: COLORS.fg,
          transition: 'left 0.2s ease',
        }}
        aria-hidden="true"
      />
    </button>
  );
};

export default CookieConsent;
